import moment from 'moment';

/**
 * Calculate program duration intelligently
 * Priority: Contract → Heures privées → Company defaults → Fallback (12 weeks)
 */
export async function getProgramDuration(userId, firestore) {
  try {
    // 1. Fetch user
    const userDoc = await firestore.collection('users').doc(userId).get();
    const user = userDoc.data();
    if (!user) throw new Error('User not found');

    // 2. Try Contract first (highest priority)
    const contractIds = user.contracts || [];
    if (contractIds.length > 0) {
      const contractDocs = await Promise.all(
        contractIds.map(id => firestore.collection('contracts').doc(id).get())
      );

      const contracts = contractDocs
        .map((doc, idx) => ({
          id: contractIds[idx],
          ...doc.data(),
          endDateObj: toDateSafe(doc.data()?.endDate)
        }))
        .filter(c => c.endDateObj);

      const today = new Date();
      const activeContracts = contracts.filter(c => c.endDateObj >= today);

      if (activeContracts.length > 0) {
        const activeContract = activeContracts.sort((a, b) =>
          b.endDateObj - a.endDateObj
        )[0];

        const startDate = toDateSafe(activeContract.startDate);
        const endDate = activeContract.endDateObj;

        if (startDate && endDate) {
          return {
            source: 'contract',
            contractId: activeContract.id,
            startDate,
            endDate,
            durationWeeks: Math.floor((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)),
            durationDays: Math.floor((endDate - startDate) / (24 * 60 * 60 * 1000))
          };
        }
      }
    }

    // 3. Try hoursPurchased (second priority)
    const hoursPurchased = user.hoursPurchased || [];
    if (hoursPurchased.length > 0) {
      const validHours = hoursPurchased
        .map(h => ({ ...h, endDateObj: toDateSafe(h.endDate) }))
        .filter(h => h.endDateObj);

      if (validHours.length > 0) {
        const latest = validHours.sort((a, b) =>
          b.endDateObj - a.endDateObj
        )[0];

        const startDate = toDateSafe(user.dateAdded) || new Date();
        const endDate = latest.endDateObj;

        return {
          source: 'hoursPurchased',
          startDate,
          endDate,
          durationWeeks: Math.floor((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)),
          durationDays: Math.floor((endDate - startDate) / (24 * 60 * 60 * 1000))
        };
      }
    }

    // 4. Fallback: Company defaults
    const companyId = user.company;
    if (companyId) {
      const companyDoc = await firestore.collection('company').doc(companyId).get();
      const company = companyDoc.data();

      if (company?.sprintDurationMonths) {
        const startDate = toDateSafe(user.dateAdded) || new Date();
        const endDate = moment(startDate).add(company.sprintDurationMonths, 'months').toDate();

        return {
          source: 'company_default',
          companyId,
          startDate,
          endDate,
          durationWeeks: Math.floor((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)),
          durationDays: Math.floor((endDate - startDate) / (24 * 60 * 60 * 1000))
        };
      }
    }

    // 5. Final fallback: 12 weeks hardcoded
    const startDate = toDateSafe(user.dateAdded) || new Date();
    const endDate = moment(startDate).add(12, 'weeks').toDate();

    return {
      source: 'hardcoded_default',
      startDate,
      endDate,
      durationWeeks: 12,
      durationDays: 84
    };

  } catch (error) {
    console.error('Error calculating program duration:', error);
    throw error;
  }
}

function toDateSafe(v) {
  try {
    if (!v) return null;
    if (typeof v?.toDate === 'function') return v.toDate();
    if (v?.seconds) return new Date(v.seconds * 1000);
    if (v instanceof Date) return v;
    if (typeof v === 'string') {
      const d = new Date(v);
      return isNaN(d.getTime()) ? null : d;
    }
  } catch (_) {}
  return null;
}

/**
 * Determine evaluation schedule based on program duration
 */
export function getEvaluationSchedule(durationWeeks) {
  const schedule = {
    initial: {
      auto: 'week 1',
      coach: 'week 1-2',
      label: 'Baseline'
    },
    final: {
      auto: `week ${durationWeeks}`,
      coach: `week ${durationWeeks}`,
      label: 'Program End'
    }
  };

  if (durationWeeks > 12) {
    schedule.midterm_1 = {
      auto: `week ${Math.floor(durationWeeks * 0.5)}`,
      coach: `week ${Math.floor(durationWeeks * 0.5)}`,
      label: 'Halfway'
    };
  }

  if (durationWeeks > 24) {
    schedule.midterm_2 = {
      auto: `week ${Math.floor(durationWeeks * 0.75)}`,
      coach: `week ${Math.floor(durationWeeks * 0.75)}`,
      label: 'Three-quarters'
    };
  }

  return schedule;
}

/**
 * Get email strategy based on program duration
 */
export function getEmailStrategy(durationWeeks) {
  if (durationWeeks <= 12) {
    return {
      tier: 'SHORT',
      focus: 'dimension improvements',
      cefrPromise: false,
      emphasis: 'Fluency + Confidence gains'
    };
  } else if (durationWeeks <= 26) {
    return {
      tier: 'MEDIUM',
      focus: 'dimension + micro-CEFR',
      cefrPromise: false,
      emphasis: 'Steady progress, B1 edge'
    };
  } else if (durationWeeks <= 52) {
    return {
      tier: 'LONG',
      focus: 'CEFR + dimension',
      cefrPromise: true,
      emphasis: 'Significant level change'
    };
  } else {
    return {
      tier: 'VERY_LONG',
      focus: 'transformation story',
      cefrPromise: true,
      emphasis: 'Full year journey'
    };
  }
}
